/**
 * ChoreTemplateForm — create / edit a chore template (admin surface, Fase 3).
 *
 * Fields map 1:1 onto the server's CreateTemplateBody / UpdateTemplateBody:
 *   title, icon (icon_key), description, point_value, min_age, proof_required,
 *   recurrence (via <RecurrencePicker>), assignee multi-select + completion_mode
 *   ("alle må" = each / "delt" = shared, from Fase 1).
 *
 * Assignee editing on edit mode is OPT-IN (mirrors MemberForm's "Endre PIN"):
 * the template list route does not echo the current assignee set, so leaving
 * the editor closed omits `assignee_ids` from the PATCH and the server keeps
 * the existing set untouched. Opening it replaces the set wholesale (an empty
 * selection clears it back to an open pool).
 *
 * completion_mode only has an effect when at least one assignee is selected;
 * the toggle is always shown but annotated so the admin understands open-pool
 * templates ignore it.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberAdminRow, Recurrence } from '@stell/shared';
import { Button, ChoreIcon } from '../../components/primitives';
import { RecurrencePicker } from './RecurrencePicker';
import type { ChoreTemplateRow } from './useChoreTemplates';

/** Chore-template icon keys recognised by <ChoreIcon> (ICON_BY_KEY). */
const ICON_KEYS = [
  'vacuum', 'dishes', 'bed', 'trash', 'laundry', 'plant',
  'dog', 'book', 'bulb', 'flame', 'therm', 'sparkle', 'check',
] as const;

export interface ChoreTemplateFormPayload {
  title: string;
  description: string | null;
  icon_key: string;
  point_value: number;
  min_age: number;
  proof_required: 'none' | 'optional' | 'required';
  recurrence: Recurrence;
  completion_mode: 'shared' | 'each';
  /** undefined = leave assignees untouched; array = replace the set. */
  assignee_ids?: string[];
  // Fase 4. kind='homework' = a one-off lekse with a subject + optional due
  // date; the form forces recurrence {type:'once'} so the server seeds it.
  kind: 'chore' | 'homework';
  subject: string | null;
  due_date: string | null;
  /**
   * Free-text grouping key for the board ("Kjøkken"). Nullable and optional by
   * design: the column and the API body have always accepted it, but nothing
   * could set it until the board started grouping by it.
   */
  category: string | null;
}

export interface ChoreTemplateFormProps {
  mode: 'create' | 'edit';
  initial?: ChoreTemplateRow | undefined;
  /** Active family roster for the assignee multi-select. */
  members: MemberAdminRow[];
  submitting: boolean;
  errorCode: string | null;
  onSubmit: (payload: ChoreTemplateFormPayload) => void;
  onCancel: () => void;
}

interface Draft {
  title: string;
  iconKey: string;
  description: string;
  pointValue: number;
  minAge: number;
  proofRequired: 'none' | 'optional' | 'required';
  recurrence: Recurrence;
  completionMode: 'shared' | 'each';
  assigneeIds: string[];
  kind: 'chore' | 'homework';
  subject: string;
  /** ISO date (yyyy-mm-dd) from <input type="date">; '' = no deadline. */
  dueDate: string;
  /** Board grouping key; '' = uncategorised. */
  category: string;
}

/** Parse the stored recurrence JSON; fall back to daily on legacy/garbage. */
function parseRecurrence(json: string | undefined): Recurrence {
  if (!json) return { type: 'daily' };
  try {
    return JSON.parse(json) as Recurrence;
  } catch {
    return { type: 'daily' };
  }
}

function initialDraft(initial?: ChoreTemplateRow): Draft {
  if (!initial) {
    return {
      title: '',
      iconKey: 'sparkle',
      description: '',
      pointValue: 10,
      minAge: 0,
      proofRequired: 'none',
      recurrence: { type: 'daily' },
      completionMode: 'shared',
      assigneeIds: [],
      kind: 'chore',
      subject: '',
      dueDate: '',
      category: '',
    };
  }
  return {
    title: initial.title,
    iconKey: initial.icon_key,
    description: initial.description ?? '',
    pointValue: initial.point_value,
    minAge: initial.min_age,
    proofRequired: initial.proof_required,
    recurrence: parseRecurrence(initial.recurrence),
    completionMode: initial.completion_mode,
    assigneeIds: [],
    kind: initial.kind ?? 'chore',
    subject: initial.subject ?? '',
    dueDate: (initial.due_date ?? '').slice(0, 10),
    category: initial.category ?? '',
  };
}

export function ChoreTemplateForm({
  mode,
  initial,
  members,
  submitting,
  errorCode,
  onSubmit,
  onCancel,
}: ChoreTemplateFormProps): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(initial));
  // On edit, the assignee set is unknown (not echoed by the list route), so
  // editing it is opt-in to avoid clobbering. Create always edits assignees.
  const [editAssignees, setEditAssignees] = useState(mode === 'create');

  const activeMembers = useMemo(
    () => members.filter((m) => !m.archived_at),
    [members],
  );

  const formValid = draft.title.trim().length > 0 && draft.pointValue >= 1;

  const errorText = useMemo(() => {
    if (!errorCode) return null;
    if (errorCode === 'invalid_body') return t('admin.choreTemplates.error.invalid');
    return t('error.offline');
  }, [errorCode, t]);

  function toggleAssignee(id: string): void {
    setDraft((d) => ({
      ...d,
      assigneeIds: d.assigneeIds.includes(id)
        ? d.assigneeIds.filter((x) => x !== id)
        : [...d.assigneeIds, id],
    }));
  }

  const isHomework = draft.kind === 'homework';

  function handleSubmit(): void {
    if (!formValid || submitting) return;
    const payload: ChoreTemplateFormPayload = {
      title: draft.title.trim(),
      description: draft.description.trim() || null,
      icon_key: draft.iconKey,
      point_value: draft.pointValue,
      min_age: draft.minAge,
      proof_required: draft.proofRequired,
      // Homework is always one-off — the server seeds it directly and rejects
      // any other recurrence for kind='homework'.
      recurrence: isHomework ? { type: 'once' } : draft.recurrence,
      completion_mode: draft.completionMode,
      kind: draft.kind,
      subject: isHomework ? (draft.subject.trim() || null) : null,
      due_date: isHomework ? (draft.dueDate || null) : null,
      category: draft.category.trim() || null,
    };
    // Only send assignee_ids when the editor is open (create, or edit + opted-in).
    if (editAssignees) {
      payload.assignee_ids = draft.assigneeIds;
    }
    onSubmit(payload);
  }

  return (
    <form
      data-testid="chore-template-form"
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit();
      }}
    >
      {/* Title */}
      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('admin.choreTemplates.field.title')}</span>
        <input
          data-testid="chore-title"
          className="field"
          type="text"
          maxLength={120}
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
      </label>

      {/* Kind: oppgave vs lekse (Fase 4). Fixed on edit — homework-ness is
          decided at creation (the instance is seeded differently). */}
      <fieldset className="flex flex-col gap-1" data-testid="chore-kind">
        <legend className="text-ink">{t('admin.choreTemplates.field.kind')}</legend>
        <div className="flex gap-4">
          {(['chore', 'homework'] as const).map((k) => (
            <label key={k} className="flex items-center gap-1">
              <input
                type="radio"
                name="kind"
                value={k}
                checked={draft.kind === k}
                disabled={mode === 'edit'}
                onChange={() => setDraft({ ...draft, kind: k })}
                data-testid={`chore-kind-${k}`}
              />
              <span>{t(`admin.choreTemplates.kind.${k}`)}</span>
            </label>
          ))}
        </div>
        {isHomework ? (
          <span className="text-caption text-ink-2">{t('admin.choreTemplates.kind.hint')}</span>
        ) : null}
      </fieldset>

      {/* Category — groups the card on the board. Applies to both kinds. */}
      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('admin.choreTemplates.field_category')}</span>
        <input
          data-testid="chore-category"
          className="field"
          type="text"
          maxLength={40}
          placeholder={t('admin.choreTemplates.categoryPlaceholder')}
          value={draft.category}
          onChange={(e) => setDraft({ ...draft, category: e.target.value })}
        />
        <span className="text-caption text-ink-2">{t('admin.choreTemplates.categoryHint')}</span>
      </label>

      {/* Homework-only: subject + due date */}
      {isHomework ? (
        <div className="flex gap-4" data-testid="chore-homework-fields">
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-ink">{t('admin.choreTemplates.field.subject')}</span>
            <input
              data-testid="chore-subject"
              className="field"
              type="text"
              maxLength={60}
              placeholder={t('admin.choreTemplates.subjectPlaceholder')}
              value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            />
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="text-ink">{t('admin.choreTemplates.field.dueDate')}</span>
            <input
              data-testid="chore-due-date"
              className="field"
              type="date"
              value={draft.dueDate}
              onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })}
            />
          </label>
        </div>
      ) : null}

      {/* Icon picker */}
      <fieldset className="flex flex-col gap-2" data-testid="chore-icon-picker">
        <legend className="text-ink">{t('admin.choreTemplates.field.icon')}</legend>
        <div className="flex flex-wrap gap-2">
          {ICON_KEYS.map((key) => {
            const selected = draft.iconKey === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`chore-icon-${key}`}
                aria-pressed={selected}
                onClick={() => setDraft({ ...draft, iconKey: key })}
                className={[
                  'rounded-md p-0.5',
                  selected ? 'ring-2 ring-primary' : 'ring-1 ring-secondary-line',
                ].join(' ')}
              >
                <ChoreIcon code={key} tintSlot="sage" size={36} />
                <span className="sr-only">{t(`admin.choreTemplates.iconLabel.${key}`)}</span>
              </button>
            );
          })}
        </div>
      </fieldset>

      {/* Description */}
      <label className="flex flex-col gap-1">
        <span className="text-ink">{t('admin.choreTemplates.field.description')}</span>
        <textarea
          data-testid="chore-description"
          className="field"
          maxLength={2000}
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </label>

      {/* Points + min age side by side */}
      <div className="flex gap-4">
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-ink">{t('admin.choreTemplates.field.points')}</span>
          <input
            data-testid="chore-points"
            className="field"
            type="number"
            min={1}
            value={draft.pointValue}
            onChange={(e) =>
              setDraft({ ...draft, pointValue: Math.max(1, Number(e.target.value) || 1) })
            }
          />
        </label>
        <label className="flex flex-1 flex-col gap-1">
          <span className="text-ink">{t('admin.choreTemplates.field.minAge')}</span>
          <input
            data-testid="chore-min-age"
            className="field"
            type="number"
            min={0}
            max={120}
            value={draft.minAge}
            onChange={(e) =>
              setDraft({ ...draft, minAge: Math.max(0, Number(e.target.value) || 0) })
            }
          />
        </label>
      </div>

      {/* Proof required */}
      <fieldset className="flex flex-col gap-1" data-testid="chore-proof">
        <legend className="text-ink">{t('admin.choreTemplates.field.proof')}</legend>
        <div className="flex gap-4">
          {(['none', 'optional', 'required'] as const).map((p) => (
            <label key={p} className="flex items-center gap-1">
              <input
                type="radio"
                name="proof_required"
                value={p}
                checked={draft.proofRequired === p}
                onChange={() => setDraft({ ...draft, proofRequired: p })}
                data-testid={`chore-proof-${p}`}
              />
              <span>{t(`admin.choreTemplates.proof.${p}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {/* Recurrence — chores only. Homework is one-off (forced to 'once'). */}
      {isHomework ? null : (
        <RecurrencePicker
          value={draft.recurrence}
          onChange={(recurrence) => setDraft({ ...draft, recurrence })}
        />
      )}

      {/* Assignees */}
      <fieldset className="flex flex-col gap-2" data-testid="chore-assignees">
        <legend className="text-ink">{t('admin.choreTemplates.field.assignees')}</legend>
        {mode === 'edit' && !editAssignees ? (
          <Button
            variant="secondary"
            onClick={() => setEditAssignees(true)}
            data-testid="chore-edit-assignees"
          >
            {t('admin.choreTemplates.editAssignees')}
          </Button>
        ) : (
          <>
            <p className="text-caption text-ink-2">{t('admin.choreTemplates.assigneesHint')}</p>
            <div className="flex flex-wrap gap-2">
              {activeMembers.map((m) => {
                const on = draft.assigneeIds.includes(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`chore-assignee-${m.id}`}
                    aria-pressed={on}
                    onClick={() => toggleAssignee(m.id)}
                    className={[
                      'min-h-11 rounded-pill px-3.5 text-sm font-bold',
                      on ? 'bg-solid text-on-solid' : 'border border-secondary-line bg-secondary text-ink',
                    ].join(' ')}
                  >
                    {m.short_name || m.name}
                  </button>
                );
              })}
            </div>

            {/* completion_mode toggle — only meaningful with assignees */}
            <fieldset className="mt-2 flex flex-col gap-1" data-testid="chore-completion-mode">
              <legend className="text-sm text-ink-2">
                {t('admin.choreTemplates.field.completionMode')}
              </legend>
              <div className="flex gap-4">
                {(['shared', 'each'] as const).map((cm) => (
                  <label key={cm} className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="completion_mode"
                      value={cm}
                      checked={draft.completionMode === cm}
                      onChange={() => setDraft({ ...draft, completionMode: cm })}
                      data-testid={`chore-completion-${cm}`}
                    />
                    <span>{t(`admin.choreTemplates.completionMode.${cm}`)}</span>
                  </label>
                ))}
              </div>
              <span className="text-caption text-ink-2">
                {t('admin.choreTemplates.completionModeHint')}
              </span>
            </fieldset>
          </>
        )}
      </fieldset>

      {errorText ? (
        <p data-testid="chore-template-form-error" className="text-sm text-bad">
          {errorText}
        </p>
      ) : null}

      <div className="flex justify-end gap-3">
        <Button variant="ghost" onClick={onCancel} data-testid="chore-template-form-cancel">
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!formValid || submitting}
          data-testid="chore-template-form-save"
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
